import { cva, type VariantProps } from 'class-variance-authority';
import { ComponentProps } from 'preact';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap border px-[5px] py-px text-xs/4 font-semibold transition-[color,box-shadow] focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a&]:hover:bg-primary/90 border-transparent',
        secondary: 'bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90 border-transparent',
        destructive:
          'bg-destructive focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90 border-transparent text-white',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',

        number: 'border-transparent p-1',

        rose: 'border-transparent bg-rose-700 text-white',
        lightGreen: 'border-transparent bg-green-500 text-white',
        green: 'border-transparent bg-green-600 text-white',
        stone: 'border-transparent bg-stone-500 text-white',
        yellow: 'border-transparent bg-primary-yellow text-forest-darkest',
        moss: 'border-transparent bg-moss-100 text-forest-darkest',
        forest: 'border-transparent bg-forest-core text-white',
        sun: 'border-transparent bg-sun-600 text-white',
        tea: 'border-transparent bg-tea-600 text-white',
        petal: 'border-transparent bg-petal-600 text-white',
        coral: 'border-transparent bg-coral-600 text-white',
      },
      roundness: {
        default: 'rounded',
        full: 'rounded-full',
      },
      shadow: {
        none: 'shadow-none',
        small: 'shadow-[0_4px_10px_0_rgba(0,0,0,0.25)]',
      },
    },
    defaultVariants: {
      variant: 'default',
      roundness: 'default',
      shadow: 'none',
    },
  },
);

function Badge({
  className,
  variant,
  roundness,
  shadow,
  ...props
}: ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <span data-slot="badge" className={cn(badgeVariants({ variant, roundness, shadow }), className)} {...props} />;
}

export { Badge, badgeVariants };
